import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../components/AppIcon';


const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const RED = '#F87171';
const AMBER = '#FBBF24';

const fmt = (v) => v || '—';
const fmtDate = (d) => d ? new Date(d)?.toLocaleDateString('es-ES') : '—';

const FORMA_PAGO_OPTS = ['CONTADO', 'TRANSFERENCIA', '30 DÍAS', '60 DÍAS', '90 DÍAS', 'CONFIRMING', 'PAGARÉ'];

const emptyForm = () => ({
  codigo: '',
  nombre: '',
  nombre_comercial: '',
  nif_cif: '',
  contacto_principal: '',
  telefono: '',
  email: '',
  direccion: '',
  codigo_postal: '',
  localidad: '',
  provincia: '',
  pais: 'España',
  forma_pago: '',
  dias_pago: 30,
  observaciones: '',
  activo: true,
});

// ─── Construir payload limpio (sin campos calculados) ─────────────────────────
const buildProveedorPayload = (form) => ({
  codigo: form?.codigo || null,
  nombre: form?.nombre || '',
  nombre_comercial: form?.nombre_comercial || null,
  nif_cif: form?.nif_cif || null,
  contacto_principal: form?.contacto_principal || null,
  telefono: form?.telefono || null,
  email: form?.email || null,
  direccion: form?.direccion || null,
  codigo_postal: form?.codigo_postal || null,
  localidad: form?.localidad || null,
  provincia: form?.provincia || null,
  pais: form?.pais || 'España',
  forma_pago: form?.forma_pago || null,
  dias_pago: form?.dias_pago != null ? parseInt(form?.dias_pago) || null : null,
  observaciones: form?.observaciones || null,
  activo: form?.activo ?? true,
});

// ─── FIELD ────────────────────────────────────────────────────────────────────
const Field = ({ label, required = false, children }) => (
  <div>
    <label className="block font-oranienbaum text-white/45 text-xs mb-1">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = 'w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none';
const inputSt = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };
const selectSt = { background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.85)' };

// ─── PROVEEDOR FORM MODAL ─────────────────────────────────────────────────────
const ProveedorFormModal = ({ proveedor, onSave, onClose }) => {
  const [form, setForm] = useState(proveedor ? {
    codigo: proveedor?.codigo || '',
    nombre: proveedor?.nombre || '',
    nombre_comercial: proveedor?.nombre_comercial || '',
    nif_cif: proveedor?.nif_cif || '',
    contacto_principal: proveedor?.contacto_principal || '',
    telefono: proveedor?.telefono || '',
    email: proveedor?.email || '',
    direccion: proveedor?.direccion || '',
    codigo_postal: proveedor?.codigo_postal || '',
    localidad: proveedor?.localidad || '',
    provincia: proveedor?.provincia || '',
    pais: proveedor?.pais || 'España',
    forma_pago: proveedor?.forma_pago || '',
    dias_pago: proveedor?.dias_pago ?? 30,
    observaciones: proveedor?.observaciones || '',
    activo: proveedor?.activo ?? true,
  } : emptyForm());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form?.nombre?.trim()) { setErr('El nombre es obligatorio.'); return; }
    try {
      setSaving(true);
      setErr(null);
      // Construir payload limpio — sin campos calculados como _numFacturas
      const payload = buildProveedorPayload(form);
      await onSave(payload);
      onClose();
    } catch (e) {
      setErr(e?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-2xl rounded-2xl flex flex-col max-h-[92vh]" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}30` }}>
              <Icon name="Building2" size={15} style={{ color: BLUE }} />
            </div>
            <h3 className="font-oranienbaum font-bold text-white text-lg">
              {proveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {err && (
            <div className="p-3 rounded-xl text-xs font-oranienbaum text-red-400 flex items-start gap-2"
              style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <Icon name="AlertCircle" size={13} className="flex-shrink-0 mt-0.5" />
              {err}
            </div>
          )}

          {/* Datos fiscales */}
          <div>
            <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-2">Datos Fiscales</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Nombre Fiscal" required>
                <input type="text" value={form?.nombre || ''} onChange={e => set('nombre', e?.target?.value)}
                  className={inputCls} style={inputSt} placeholder="Razón social" />
              </Field>
              <Field label="Nombre Comercial">
                <input type="text" value={form?.nombre_comercial || ''} onChange={e => set('nombre_comercial', e?.target?.value)}
                  className={inputCls} style={inputSt} placeholder="Nombre comercial" />
              </Field>
              <Field label="NIF / CIF">
                <input type="text" value={form?.nif_cif || ''} onChange={e => set('nif_cif', e?.target?.value)}
                  className={inputCls} style={inputSt} placeholder="B12345678" />
              </Field>
              <Field label="Código Interno">
                <input type="text" value={form?.codigo || ''} onChange={e => set('codigo', e?.target?.value)}
                  className={inputCls} style={inputSt} placeholder="PROV-001" />
              </Field>
            </div>
          </div>

          {/* Contacto */}
          <div>
            <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-2">Contacto</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Persona de Contacto">
                <input type="text" value={form?.contacto_principal || ''} onChange={e => set('contacto_principal', e?.target?.value)}
                  className={inputCls} style={inputSt} placeholder="Nombre del contacto" />
              </Field>
              <Field label="Teléfono">
                <input type="tel" value={form?.telefono || ''} onChange={e => set('telefono', e?.target?.value)}
                  className={inputCls} style={inputSt} placeholder="+34 600 000 000" />
              </Field>
              <Field label="Email" >
                <input type="email" value={form?.email || ''} onChange={e => set('email', e?.target?.value)}
                  className={inputCls} style={inputSt} placeholder="proveedor@email.com" />
              </Field>
            </div>
          </div>

          {/* Dirección */}
          <div>
            <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-2">Dirección</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Field label="Dirección">
                  <input type="text" value={form?.direccion || ''} onChange={e => set('direccion', e?.target?.value)}
                    className={inputCls} style={inputSt} placeholder="Calle, número, piso..." />
                </Field>
              </div>
              <Field label="Código Postal">
                <input type="text" value={form?.codigo_postal || ''} onChange={e => set('codigo_postal', e?.target?.value)}
                  className={inputCls} style={inputSt} placeholder="28001" />
              </Field>
              <Field label="Localidad">
                <input type="text" value={form?.localidad || ''} onChange={e => set('localidad', e?.target?.value)}
                  className={inputCls} style={inputSt} placeholder="Madrid" />
              </Field>
              <Field label="Provincia">
                <input type="text" value={form?.provincia || ''} onChange={e => set('provincia', e?.target?.value)}
                  className={inputCls} style={inputSt} placeholder="Madrid" />
              </Field>
              <Field label="País">
                <input type="text" value={form?.pais || ''} onChange={e => set('pais', e?.target?.value)}
                  className={inputCls} style={inputSt} placeholder="España" />
              </Field>
            </div>
          </div>

          {/* Condiciones de pago */}
          <div>
            <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-2">Condiciones de Pago</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Forma de Pago">
                <select value={form?.forma_pago || ''} onChange={e => set('forma_pago', e?.target?.value)}
                  className={inputCls} style={selectSt}>
                  <option value="">— Seleccionar —</option>
                  {FORMA_PAGO_OPTS?.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Días de Pago">
                <input type="number" min="0" value={form?.dias_pago ?? 30} onChange={e => set('dias_pago', parseInt(e?.target?.value) || 0)}
                  className={inputCls} style={inputSt} />
              </Field>
            </div>
          </div>

          {/* Observaciones */}
          <Field label="Observaciones">
            <textarea value={form?.observaciones || ''} onChange={e => set('observaciones', e?.target?.value)}
              rows={3} className={inputCls} style={{ ...inputSt, resize: 'none' }}
              placeholder="Notas internas sobre el proveedor..." />
          </Field>

          {/* Activo */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => set('activo', !form?.activo)}
              className="w-10 h-5 rounded-full transition-all flex-shrink-0 relative"
              style={{ background: form?.activo ? `${ACCENT}40` : 'rgba(255,255,255,0.10)', border: `1px solid ${form?.activo ? ACCENT : 'rgba(255,255,255,0.15)'}` }}
            >
              <span className="absolute top-0.5 w-4 h-4 rounded-full transition-all"
                style={{ background: form?.activo ? ACCENT : 'rgba(255,255,255,0.40)', left: form?.activo ? '20px' : '1px' }} />
            </button>
            <span className="font-oranienbaum text-white/60 text-sm">Proveedor activo</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE }}>
            {saving ? <><div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-[#60A5FA] animate-spin" />Guardando...</> : <><Icon name="Save" size={14} />Guardar</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── PROVEEDOR DETAIL PANEL ───────────────────────────────────────────────────
const ProveedorDetailPanel = ({ proveedor, onEdit, onClose }) => {
  const Row = ({ label, val }) => (
    <div className="flex items-start gap-2 py-1.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <span className="font-oranienbaum text-white/35 text-xs w-32 flex-shrink-0">{label}</span>
      <span className="font-oranienbaum text-white/75 text-xs">{val || '—'}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.80)' }}>
      <div className="w-full max-w-lg rounded-2xl flex flex-col max-h-[90vh]" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
              <Icon name="Building2" size={16} style={{ color: ACCENT }} />
            </div>
            <div>
              <h3 className="font-oranienbaum font-bold text-white text-base">{proveedor?.nombre}</h3>
              {proveedor?.nombre_comercial && <p className="font-oranienbaum text-white/40 text-xs">{proveedor?.nombre_comercial}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-1">
          <Row label="NIF / CIF" val={proveedor?.nif_cif} />
          <Row label="Código" val={proveedor?.codigo} />
          <Row label="Contacto" val={proveedor?.contacto_principal} />
          <Row label="Teléfono" val={proveedor?.telefono} />
          <Row label="Email" val={proveedor?.email} />
          <Row label="Dirección" val={proveedor?.direccion} />
          <Row label="C.P." val={proveedor?.codigo_postal} />
          <Row label="Localidad" val={proveedor?.localidad} />
          <Row label="Provincia" val={proveedor?.provincia} />
          <Row label="País" val={proveedor?.pais} />
          <Row label="Forma de Pago" val={proveedor?.forma_pago} />
          <Row label="Días de Pago" val={proveedor?.dias_pago != null ? `${proveedor?.dias_pago} días` : null} />
          <Row label="Observaciones" val={proveedor?.observaciones} />
          <Row label="Estado" val={proveedor?.activo ? 'Activo' : 'Inactivo'} />
          {/* Estadísticas calculadas (solo lectura, no se guardan) */}
          {proveedor?._numFacturas != null && (
            <>
              <div className="pt-2">
                <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-1">Estadísticas</p>
              </div>
              <Row label="Nº Facturas" val={proveedor?._numFacturas} />
              <Row label="Total Comprado" val={typeof proveedor?._totalComprado === 'number' ? `${proveedor?._totalComprado?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €` : null} />
              <Row label="Pendiente" val={typeof proveedor?._pendiente === 'number' ? `${proveedor?._pendiente?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €` : null} />
            </>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            Cerrar
          </button>
          <button onClick={() => onEdit(proveedor)}
            className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold flex items-center justify-center gap-2"
            style={{ background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE }}>
            <Icon name="Edit2" size={13} />Editar
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const TiendaProveedores = () => {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editProveedor, setEditProveedor] = useState(null);
  const [detailProveedor, setDetailProveedor] = useState(null);
  const [showInactivos, setShowInactivos] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { tiendaDb } = await import('../../lib/supabase');
      const [provRes, factRes] = await Promise.all([
        tiendaDb?.from('proveedores')
          ?.select('id, codigo, nombre, nombre_comercial, nif_cif, contacto_principal, telefono, email, activo, direccion, codigo_postal, localidad, provincia, pais, forma_pago, dias_pago, observaciones')
          ?.order('nombre'),
        tiendaDb?.from('facturas_compra')
          ?.select('id, proveedor_id, total, pendiente_pago, estado_pago, fecha_vencimiento'),
      ]);
      if (provRes?.error) throw provRes?.error;
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const facturas = factRes?.data || [];
      // Enriquecer con estadísticas calculadas — estos campos NO se envían al update
      const proveedoresConStats = (provRes?.data || [])?.map(p => {
        const pFacts = facturas?.filter(f => f?.proveedor_id === p?.id);
        const pendiente = pFacts?.filter(f => f?.estado_pago !== 'PAGADA' && f?.estado_pago !== 'ANULADA')
          ?.reduce((s, f) => s + (parseFloat(f?.pendiente_pago ?? f?.total ?? 0)), 0);
        const vencido = pFacts?.filter(f => f?.estado_pago !== 'PAGADA' && f?.estado_pago !== 'ANULADA' && f?.fecha_vencimiento && f?.fecha_vencimiento < today)
          ?.reduce((s, f) => s + (parseFloat(f?.pendiente_pago ?? f?.total ?? 0)), 0);
        return {
          ...p,
          // Campos calculados con prefijo _ — solo para visualización
          _numFacturas: pFacts?.length,
          _totalComprado: pFacts?.reduce((s, f) => s + (f?.total || 0), 0),
          _pendiente: pendiente,
          _vencido: vencido,
        };
      });
      setProveedores(proveedoresConStats);
    } catch (e) {
      setError(e?.message || 'Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = proveedores?.filter(p => {
    if (!showInactivos && !p?.activo) return false;
    if (!search) return true;
    const s = search?.toLowerCase();
    return (
      p?.nombre?.toLowerCase()?.includes(s) ||
      (p?.nombre_comercial || '')?.toLowerCase()?.includes(s) ||
      (p?.nif_cif || '')?.toLowerCase()?.includes(s) ||
      (p?.localidad || '')?.toLowerCase()?.includes(s) ||
      (p?.email || '')?.toLowerCase()?.includes(s)
    );
  });

  const paged = filtered?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(filtered?.length / PAGE_SIZE);

  const handleSave = async (payload) => {
    // payload ya viene limpio desde buildProveedorPayload — sin campos calculados
    const { tiendaDb } = await import('../../lib/supabase');
    if (editProveedor) {
      const { error } = await tiendaDb?.from('proveedores')?.update(payload)?.eq('id', editProveedor?.id);
      if (error) throw error;
    } else {
      const { error } = await tiendaDb?.from('proveedores')?.insert(payload);
      if (error) throw error;
    }
    await loadData();
  };

  const openNew = () => { setEditProveedor(null); setShowForm(true); };
  const openEdit = (p) => {
    // Pasar solo los campos persistentes al modal de edición
    setEditProveedor({
      id: p?.id,
      codigo: p?.codigo,
      nombre: p?.nombre,
      nombre_comercial: p?.nombre_comercial,
      nif_cif: p?.nif_cif,
      contacto_principal: p?.contacto_principal,
      telefono: p?.telefono,
      email: p?.email,
      direccion: p?.direccion,
      codigo_postal: p?.codigo_postal,
      localidad: p?.localidad,
      provincia: p?.provincia,
      pais: p?.pais,
      forma_pago: p?.forma_pago,
      dias_pago: p?.dias_pago,
      observaciones: p?.observaciones,
      activo: p?.activo,
    });
    setDetailProveedor(null);
    setShowForm(true);
  };

  const kpis = [
    { label: 'Total Proveedores', value: proveedores?.filter(p => p?.activo)?.length, icon: 'Building2', color: ACCENT },
    { label: 'Con Email', value: proveedores?.filter(p => p?.activo && p?.email)?.length, icon: 'Mail', color: BLUE },
    { label: 'Con NIF/CIF', value: proveedores?.filter(p => p?.activo && p?.nif_cif)?.length, icon: 'FileText', color: GOLD },
    { label: 'Inactivos', value: proveedores?.filter(p => !p?.activo)?.length, icon: 'Archive', color: 'rgba(255,255,255,0.30)' },
  ];

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Modals */}
      {showForm && (
        <ProveedorFormModal
          proveedor={editProveedor}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditProveedor(null); }}
        />
      )}
      {detailProveedor && (
        <ProveedorDetailPanel
          proveedor={detailProveedor}
          onEdit={openEdit}
          onClose={() => setDetailProveedor(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
              <Icon name="Building2" size={18} style={{ color: ACCENT }} />
            </div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Proveedores</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-12">Directorio de proveedores · tienda.proveedores</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum transition-all"
            style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30`, color: ACCENT }}>
            <Icon name="Plus" size={15} />Nuevo Proveedor
          </button>
          <button onClick={loadData}
            className="p-2.5 rounded-xl transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            <Icon name="RefreshCw" size={14} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {kpis?.map((k, i) => (
          <div key={i} className="rounded-xl p-4 flex items-center gap-3"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${k?.color}15`, border: `1px solid ${k?.color}25` }}>
              <Icon name={k?.icon} size={16} style={{ color: k?.color }} />
            </div>
            <div>
              <p className="font-oranienbaum font-bold text-white text-xl">{loading ? '...' : k?.value}</p>
              <p className="font-oranienbaum text-white/45 text-xs">{k?.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="rounded-2xl p-4 mb-4 flex flex-wrap gap-3 items-center"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" placeholder="Buscar por nombre, NIF, localidad, email..."
            value={search} onChange={e => { setSearch(e?.target?.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 placeholder-white/25 outline-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
        </div>
        <button
          onClick={() => setShowInactivos(p => !p)}
          className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-oranienbaum transition-all"
          style={showInactivos
            ? { background: `${AMBER}15`, border: `1px solid ${AMBER}30`, color: AMBER }
            : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.50)' }}>
          <Icon name="Archive" size={13} />
          {showInactivos ? 'Ocultar inactivos' : 'Mostrar inactivos'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 rounded-xl flex items-center gap-3"
          style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="px-4 py-3 flex items-center justify-between"
          style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <span className="font-oranienbaum text-white/50 text-xs">{filtered?.length} proveedores</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Nombre', 'NIF/CIF', 'Contacto', 'Teléfono', 'Email', 'Localidad', 'Forma Pago', 'Facturas', 'Total Comprado', 'Pendiente', 'Vencido', 'Estado', 'Acciones']?.map(col => (
                  <th key={col} className="px-3 py-3 text-left whitespace-nowrap">
                    <span className="font-oranienbaum text-white/40 text-xs uppercase tracking-wider">{col}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={13} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" />
                    <p className="font-oranienbaum text-white/40 text-sm">Cargando proveedores...</p>
                  </div>
                </td></tr>
              ) : paged?.length === 0 ? (
                <tr><td colSpan={13} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                      style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}15` }}>
                      <Icon name="Building2" size={24} style={{ color: `${ACCENT}40` }} />
                    </div>
                    <p className="font-oranienbaum text-white/40 text-sm">No hay proveedores</p>
                    <button onClick={openNew}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-oranienbaum"
                      style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30`, color: ACCENT }}>
                      <Icon name="Plus" size={13} />Añadir primer proveedor
                    </button>
                  </div>
                </td></tr>
              ) : paged?.map((p, i) => (
                <tr key={p?.id}
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)', opacity: p?.activo ? 1 : 0.5 }}
                  className="hover:bg-white/[0.03] transition-colors">
                  <td className="px-3 py-3">
                    <div>
                      <p className="font-oranienbaum text-white/85 text-sm font-semibold">{p?.nombre}</p>
                      {p?.nombre_comercial && p?.nombre_comercial !== p?.nombre && (
                        <p className="font-oranienbaum text-white/35 text-xs">{p?.nombre_comercial}</p>
                      )}
                      {p?.codigo && <p className="font-oranienbaum text-white/25 text-[10px]">{p?.codigo}</p>}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-oranienbaum text-white/60 text-xs">{fmt(p?.nif_cif)}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-oranienbaum text-white/60 text-xs">{fmt(p?.contacto_principal)}</span>
                  </td>
                  <td className="px-3 py-3">
                    {p?.telefono
                      ? <a href={`tel:${p?.telefono}`} className="font-oranienbaum text-blue-400 text-xs hover:underline">{p?.telefono}</a>
                      : <span className="text-white/25 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-3">
                    {p?.email
                      ? <a href={`mailto:${p?.email}`} className="font-oranienbaum text-blue-400 text-xs hover:underline truncate block max-w-[160px]">{p?.email}</a>
                      : <span className="text-white/25 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-oranienbaum text-white/50 text-xs">{p?.localidad || '—'}</span>
                  </td>
                  <td className="px-3 py-3">
                    {p?.forma_pago
                      ? <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum"
                          style={{ background: `${BLUE}12`, color: BLUE, border: `1px solid ${BLUE}25` }}>
                          {p?.forma_pago}{p?.dias_pago ? ` · ${p?.dias_pago}d` : ''}
                        </span>
                      : <span className="text-white/25 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-oranienbaum text-white/60 text-xs">{p?._numFacturas ?? 0}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-oranienbaum text-white/70 text-xs">{typeof p?._totalComprado === 'number' ? `${p?._totalComprado?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '—'}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-oranienbaum text-xs font-semibold" style={{ color: (p?._pendiente ?? 0) > 0 ? '#FBBF24' : '#34D399' }}>
                      {typeof p?._pendiente === 'number' ? `${p?._pendiente?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '—'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-oranienbaum text-xs font-semibold" style={{ color: (p?._vencido ?? 0) > 0 ? '#F87171' : 'rgba(255,255,255,0.30)' }}>
                      {typeof p?._vencido === 'number' ? `${p?._vencido?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '—'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum"
                      style={p?.activo
                        ? { background: `${ACCENT}12`, color: ACCENT, border: `1px solid ${ACCENT}25` }
                        : { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.30)', border: '1px solid rgba(255,255,255,0.10)' }}>
                      {p?.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setDetailProveedor(p)} title="Ver detalle"
                        className="p-1.5 rounded-lg text-white/30 hover:text-blue-400 transition-colors"
                        style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                        <Icon name="Eye" size={12} />
                      </button>
                      <button onClick={() => openEdit(p)} title="Editar"
                        className="p-1.5 rounded-lg text-white/30 hover:text-yellow-400 transition-colors"
                        style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                        <Icon name="Edit2" size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 flex items-center justify-between"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <span className="font-oranienbaum text-white/30 text-xs">
            {filtered?.length === 0 ? '0 resultados' : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered?.length)} de ${filtered?.length}`}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30"
              style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
              <Icon name="ChevronLeft" size={14} />
            </button>
            <span className="px-3 py-1 rounded-lg font-oranienbaum text-xs" style={{ background: `${ACCENT}15`, color: ACCENT }}>{page}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30"
              style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
              <Icon name="ChevronRight" size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TiendaProveedores;
